import React, { useState } from "react";
import { NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";
import { Icon } from "@/components/kit";
import { api, isOwner } from "@/api";
import { cn } from "@/lib/utils";

export function RootProtected({ children }) {
  const location = useLocation();
  if (!api.hasToken() || !isOwner()) return <Navigate to="/root" replace state={{ from: location }} />;
  return children;
}

const links = [
  { to: "/root/accounts", icon: "users", label: "Comptes" },
  { to: "/root/partners", icon: "award", label: "Partenaires" },
];

const NavItems = ({ onClick }) => (
  <>
    {links.map((l) => (
      <NavLink
        key={l.to}
        to={l.to}
        onClick={onClick}
        data-testid={`root-nav-${l.icon}`}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[14px] font-medium transition-colors",
            isActive ? "bg-ink text-white" : "text-ink-2 hover:bg-paper-3"
          )
        }
      >
        <Icon name={l.icon} size={18} /> {l.label}
      </NavLink>
    ))}
  </>
);

export function RootShell({ children }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const logout = () => { api.adminLogout(); navigate("/root", { replace: true }); };

  return (
    <div className="min-h-screen bg-paper lg:flex">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-line bg-white p-4 lg:flex">
        <div className="mb-6 mt-2 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-ink text-white"><Icon name="shield" size={18} /></span>
          <span className="font-display text-[17px] font-semibold text-ink">Super Admin</span>
        </div>
        <nav className="flex flex-1 flex-col gap-1"><NavItems /></nav>
        <div className="flex flex-col gap-1 border-t border-divider pt-3">
          <button onClick={() => navigate("/")} data-testid="root-back-home" className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[14px] font-medium text-ink-2 hover:bg-paper-3">
            <Icon name="home" size={18} /> Accueil
          </button>
          <button onClick={logout} data-testid="root-logout" className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[14px] font-medium text-err hover:bg-paper-3">
            <Icon name="log-out" size={18} /> Déconnexion
          </button>
        </div>
      </aside>

      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-line bg-white px-4 py-3 lg:hidden">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-ink text-white"><Icon name="shield" size={16} /></span>
          <span className="font-display text-[15px] font-semibold text-ink">Super Admin</span>
        </div>
        <button onClick={() => setOpen((o) => !o)} data-testid="root-menu-toggle" className="flex h-10 w-10 items-center justify-center rounded-full border border-line">
          <Icon name={open ? "x" : "menu"} size={20} className="text-ink" />
        </button>
      </div>
      {open && (
        <div className="border-b border-line bg-white p-3 lg:hidden">
          <nav className="flex flex-col gap-1"><NavItems onClick={() => setOpen(false)} /></nav>
          <div className="mt-2 flex flex-col gap-1 border-t border-divider pt-2">
            <button onClick={() => navigate("/")} className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[14px] font-medium text-ink-2">
              <Icon name="home" size={18} /> Accueil
            </button>
            <button onClick={logout} className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[14px] font-medium text-err">
              <Icon name="log-out" size={18} /> Déconnexion
            </button>
          </div>
        </div>
      )}

      <main className="min-w-0 flex-1">{children}</main>
    </div>
  );
}
